import { PermissionEnum } from '@Application/Security/Roles';
import { parseInterviewProcessingState } from '@Domain/Enums/Interviews/InterviewProcessingState';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import GridCard from '@Guidelines/Component/GridCard';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import ArrowLink from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/ArrowLink';
import TranscriptionIssue from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/TranscriptionIssue';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type TranscriptionIssueView from '@UI/Store/WorkflowRuns/Views/TranscriptionIssueView';
import { css } from '@emotion/react';
import * as React from 'react';

import transcriptPendingPresentations, { type Tone } from './Helpers/transcriptPendingPresentations';

const TILE_COLORS: Readonly<Record<Tone, { readonly background: string; readonly color: string }>> =
	Object.freeze({
		accent: { background: theme.colors.accentSurface, color: theme.colors.accentDark },
		warning: { background: theme.states.warning.surface, color: theme.states.warning.ink },
		danger: { background: theme.states.danger.surface, color: theme.states.danger.ink },
		muted: { background: theme.colors.surfaceMuted, color: theme.colors.inkMuted },
	});

const bodyStyle = css`
	display: flex;
	align-items: center;
	gap: 1rem;
	flex-wrap: wrap;
`;

const tileStyle = (tone: Tone) => css`
	display: grid;
	place-items: center;
	width: 2.5rem;
	height: 2.5rem;
	flex: none;
	border-radius: ${theme.radii.medium};
	background-color: ${TILE_COLORS[tone].background};
	color: ${TILE_COLORS[tone].color};
`;

const messageStyle = css`
	flex: 1;
	min-width: 12rem;
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	/** The preparation where the transcription is followed, when the interview is in one. */
	readonly preparationId: number | null;
	readonly transcriptionIssue: TranscriptionIssueView | null;
	/** The server's state value, as sent. */
	readonly processingStateValue: string;
	readonly isWritable: boolean;
};

/**
 * Stands in for the transcript while there is none, and says why: where the interview stands, and the
 * page that can move it forward. Choosing a chain is a write, so it is offered only where it is allowed.
 * An interview in a preparation is followed there; otherwise a transcription problem is shown, with its
 * retry.
 */
const TranscriptPendingNotice: React.FC<Props> = ({
	subject,
	interviewId,
	preparationId,
	transcriptionIssue,
	processingStateValue,
	isWritable,
}) => {
	const state = parseInterviewProcessingState(processingStateValue);
	const presentation = state === null ? null : transcriptPendingPresentations[state];

	const link = React.useMemo(() => {
		const uploadHref = `${buildSubjectPath(subject)}/interviews/${interviewId}/upload`;

		switch (presentation?.destination) {
			case 'upload':
				return <ArrowLink to={uploadHref}>Voir l'envoi</ArrowLink>;
			case 'workflow-choice':
				return (
					<ConditionalLoader data={isWritable}>
						<WithPermission permission={PermissionEnum.INTERVIEW_WRITE}>
							<ArrowLink to={uploadHref}>Choisir la chaîne</ArrowLink>
						</WithPermission>
					</ConditionalLoader>
				);
			default:
				return null;
		}
	}, [presentation, subject, interviewId, isWritable]);

	return (
		<ConditionalLoader data={presentation}>
			{(shown) => (
				<GridCard layout="stacked" rowGap="0.75rem">
					<div css={bodyStyle}>
						<span css={tileStyle(shown.tone)} aria-hidden="true">
							<shown.icon size={20} />
						</span>
						<p css={messageStyle}>
							{preparationId !== null
								? 'La transcription se suit dans la préparation.'
								: shown.message}
						</p>
						<div>
							<ConditionalLoader data={preparationId}>
								{(id) => (
									<ArrowLink to={`${buildSubjectPath(subject)}/preparations/${id}`}>
										Voir la préparation
									</ArrowLink>
								)}
							</ConditionalLoader>
							<ConditionalLoader data={preparationId === null}>{link}</ConditionalLoader>
						</div>
					</div>
					<ConditionalLoader data={preparationId === null ? transcriptionIssue : null}>
						{(issue) => <TranscriptionIssue subject={subject} issue={issue} />}
					</ConditionalLoader>
				</GridCard>
			)}
		</ConditionalLoader>
	);
};

export default TranscriptPendingNotice;
